import { useEffect, useMemo, useRef, useState } from "react";
import {
  bulkPatchAdminComplexes,
  deleteAdminComplex,
  fetchAdminComplexById,
  fetchSipClientOptions,
  SIP_CLIENT_DIRECTORY_CHANGED_EVENT,
  searchAdminComplexes,
} from "../../../admin/api";
import EditComplexModal from "../EditComplexModal";
import { adminStyles as styles } from "../shared/adminStyles";
import {
  FIELD_SEARCH_FIELDS,
  FIELD_SEARCH_LABELS,
  FIELD_SEARCH_OPERATORS,
  FIELD_SEARCH_OPERATOR_LABELS,
  buildSearchFilter,
  createFieldFilter,
  getActiveFieldFilters,
  isPlainObject,
  normalizeFieldFilters,
  parseJsonQuery,
} from "../utils/adminFilters";
import { getCityName } from "../utils/adminFormatters";
import { getCurrentSipClientLabel, normalizeStatus } from "../utils/sipHelpers";
import WorkspaceSelectionToggle from "../workspaceSelection/WorkspaceSelectionToggle";
import { createComplexSelectionItem } from "../workspaceSelection/workspaceSelectionUtils";
import TemporarySelectionBar from "../workspaceSelection/TemporarySelectionBar";
import TemporarySelectionCheckbox from "../workspaceSelection/TemporarySelectionCheckbox";
import useTemporarySelection from "../workspaceSelection/useTemporarySelection";
import SavedViewsControls from "../savedViews/SavedViewsControls";
import BulkComplexTableEditorAction from "../bulkComplexActions/BulkComplexTableEditorAction";
import { applyBulkComplexResults } from "../bulkComplexActions/bulkComplexEditorUtils";
import { useAdminNotifications } from "../notifications/AdminNotificationsContext";
import { useAdminDataRefresh } from "../dataRefresh/AdminDataRefreshContext";
import AdminStatusBadge from "../shared/AdminStatusBadge";
import { htmlToPlainText } from "../../../utils/htmlToPlainText";
import ActionIconButton from "../shared/ActionIconButton";
import { useAdminTextSearch, useDebouncedValue } from "../utils/useAdminTextSearch";
import "./ComplexesDatabaseSection.css";
import { useCities } from "../../../providers/CitiesContext";
import { useWorkspaceSelection } from "../workspaceSelection/WorkspaceSelectionContext";
import { useWorkspaceSelectionMode } from "../workspaceSelection/WorkspaceSelectionModeContext";
import WorkspaceSelectionModeBar from "../workspaceSelection/WorkspaceSelectionModeBar";
import { createCloneComplexFormState, createEmptyComplexFormState } from "../utils/complexFormState";
const DEFAULT_COMPLEXES_VIEW = {
  search: "",
  filters: { category: "", status: "", fieldFilters: [] },
  sort: { field: "name", direction: "asc" },
  pageSize: 100,
  settings: { viewMode: "table" },
};
const SAFE_FILTERED_UPDATE_FIELDS = new Set(["name", "city", "category", "developer", "location", "presetBase"]);
export const COMPLEX_TABLE_COLUMN_WIDTHS = [17.5, 10, 27.5, 7.5, 7.5, 7.5, 10, 12.5];
export default function ComplexesDatabaseSection({ openTarget, onOpenTargetHandled }) {
  const { cityNameByKey } = useCities();
  const copyFeedbackTimeoutRef = useRef(null);
  const restoredWorkspaceViewRef = useRef(false);
  const [complexes, setComplexes] = useState([]);
  const [editingComplex, setEditingComplex] = useState(null);
  const [formMode, setFormMode] = useState("create");
  const [formSessionId, setFormSessionId] = useState(0);
  const [copiedComplexId, setCopiedComplexId] = useState(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [fieldFilters, setFieldFilters] = useState(() => [createFieldFilter()]);
  const [sipClientOptions, setSipClientOptions] = useState([]);
  const [isFieldSearchOpen, setIsFieldSearchOpen] = useState(false);
  const [jsonQueryValue] = useState("");
  const [viewMode, setViewMode] = useState("table");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTotal, setSearchTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [bulkSetValue, setBulkSetValue] = useState("");
  const [bulkError, setBulkError] = useState("");
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [workspaceFoundIds, setWorkspaceFoundIds] = useState([]);
  const { notify } = useAdminNotifications();
  const { items: workspaceItems } = useWorkspaceSelection();
  const { activeGroup, activeView, restoreState, exitMode, registerSectionState } = useWorkspaceSelectionMode();
  const isWorkspaceMode = activeGroup === "complexes";
  const workspaceComplexItems = useMemo(() => workspaceItems.filter((item) => item.entityType === "complex"), [workspaceItems]);
  const workspaceIds = useMemo(() => workspaceComplexItems.map((item) => String(item.complexId)), [workspaceComplexItems]);
  const workspaceIdSet = useMemo(() => new Set(workspaceIds), [workspaceIds]);
  const { invalidate, versions } = useAdminDataRefresh();
  const debouncedQuery = useAdminTextSearch(query);
  const openComplexForm = (mode, complex) => {
    setFormMode(mode);
    setFormSessionId((current) => current + 1);
    setEditingComplex(complex);
  };
  useEffect(() => {
    if (!openTarget?.complexId) return;
    let active = true;
    fetchAdminComplexById(openTarget.complexId).then((item) => {
      if (active) openComplexForm("edit", { ...item, _focusEmbeddedSipClientId: openTarget.embeddedSipClientId || "" });
    }).catch((loadError) => { if (active) setError(loadError.message || "Не удалось открыть ЖК"); }).finally(() => {
      if (active) onOpenTargetHandled?.();
    });
    return () => { active = false; };
  }, [openTarget, onOpenTargetHandled]);
  const activeFieldFilters = useMemo(() => getActiveFieldFilters(fieldFilters), [fieldFilters]);
  const immediateFieldFiltersKey = useMemo(
    () => JSON.stringify(activeFieldFilters.filter((filter) => ["exists", "empty"].includes(filter.operator) || filter.field === "currentClientId")),
    [activeFieldFilters]
  );
  const textFieldFiltersKey = useMemo(
    () => JSON.stringify(activeFieldFilters.filter((filter) => !["exists", "empty"].includes(filter.operator) && filter.field !== "currentClientId")),
    [activeFieldFilters]
  );
  const debouncedTextFieldFiltersKey = useDebouncedValue(textFieldFiltersKey);

  useEffect(() => {
    let active = true;
    const loadOptions = () => fetchSipClientOptions()
      .then((data) => {
        if (active) setSipClientOptions(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {
        if (active) setSipClientOptions([]);
      });
    loadOptions();
    window.addEventListener(SIP_CLIENT_DIRECTORY_CHANGED_EVENT, loadOptions);
    return () => { active = false; window.removeEventListener(SIP_CLIENT_DIRECTORY_CHANGED_EVENT, loadOptions); };
  }, []);

  const parsedJsonQuery = useMemo(
    () => parseJsonQuery(jsonQueryValue),
    [jsonQueryValue]
  );

  useEffect(() => {
    if (parsedJsonQuery.error) {
      return undefined;
    }

    let active = true;
    const controller = new AbortController();
    const runSearch = async () => {
      try {
        setIsLoading(true);
        setError("");

        const filter = buildSearchFilter({
          category,
          status,
          jsonQuery: parsedJsonQuery.query,
        });

        const data = await searchAdminComplexes({
          filter,
          fieldFilters: [...JSON.parse(immediateFieldFiltersKey), ...JSON.parse(debouncedTextFieldFiltersKey)],
          queryText: debouncedQuery.value,
          limit,
          skip: (page - 1) * limit,
          sort: { name: 1 },
          ...(isWorkspaceMode ? { workspaceIds } : {}),
        }, { signal: controller.signal });

        if (!active) return;

        const nextTotal = Number.isFinite(data?.total) ? data.total : 0;
        const nextTotalPages = Math.max(1, Math.ceil(nextTotal / limit));

        if (page > nextTotalPages) {
          setSearchTotal(nextTotal);
          setPage(nextTotalPages);
          return;
        }

        setComplexes(Array.isArray(data?.items) ? data.items : []);
        setSearchTotal(nextTotal);
        setWorkspaceFoundIds(Array.isArray(data?.workspaceFoundIds) ? data.workspaceFoundIds : []);
      } catch (searchError) {
        if (!active || searchError?.name === "AbortError") return;
        setError(searchError instanceof Error ? searchError.message : "Не удалось выполнить поиск");
      } finally {
        if (active) setIsLoading(false);
      }
    };
    runSearch();

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    category,
    status,
    debouncedTextFieldFiltersKey,
    immediateFieldFiltersKey,
    debouncedQuery.value,
    page,
    limit,
    parsedJsonQuery,
    versions.complexes,
    isWorkspaceMode,
    workspaceIds,
  ]);

  const visibleSelectionItems = useMemo(
    () =>
      complexes.map((complex) =>
        createComplexSelectionItem(complex, getCityName(complex.city, cityNameByKey))
      ),
    [cityNameByKey, complexes]
  );
  const filteredComplexes = complexes;
  const temporarySelection = useTemporarySelection(visibleSelectionItems);
  const { replaceSelection: replaceTemporarySelection } = temporarySelection;
  const currentSavedView = useMemo(() => ({
    search: query,
    filters: { category, status, fieldFilters },
    sort: { field: "name", direction: "asc" },
    pageSize: limit,
    settings: { viewMode },
  }), [category, fieldFilters, limit, query, status, viewMode]);
  const applySavedView = (view) => {
    setQuery(view.search || "");
    setCategory(view.filters?.category || "");
    setStatus(view.filters?.status || "");
    setFieldFilters(normalizeFieldFilters(view.filters?.fieldFilters, view.filters));
    setLimit([50, 100, 200, 500].includes(Number(view.pageSize)) ? Number(view.pageSize) : 100);
    setViewMode(view.settings?.viewMode === "json" ? "json" : "table");
    setPage(1);
  };
  useEffect(() => {
    if (isWorkspaceMode && activeView && !restoredWorkspaceViewRef.current) {
      restoredWorkspaceViewRef.current = true;
      applySavedView(activeView);
    }
  }, [activeView, isWorkspaceMode]);
  useEffect(() => {
    registerSectionState("complexes", currentSavedView);
  }, [currentSavedView, registerSectionState]);
  useEffect(() => {
    if (!isWorkspaceMode) return;
    replaceTemporarySelection(visibleSelectionItems.filter((item) => workspaceIdSet.has(String(item.complexId))));
  }, [isWorkspaceMode, replaceTemporarySelection, visibleSelectionItems, workspaceIdSet]);
  const exitWorkspaceMode = () => {
    if (restoreState) applySavedView(restoreState);
    exitMode();
  };
  const handleBulkComplexApplied = (results) => {
    setComplexes((current) => applyBulkComplexResults(current, results));
    if (results?.some((item) => item.ok)) invalidate(["complexes"]);
  };

  const handleSaved = (savedComplex, options = {}) => {
    const shouldClose = options.close !== false;

    if (!savedComplex?._id) {
      if (shouldClose) setEditingComplex(null);
      return;
    }

    const isNewComplex = !complexes.some((complex) => complex._id === savedComplex._id);
    if (isNewComplex) setSearchTotal((current) => current + 1);

    setComplexes((prev) => {
      const exists = prev.some((complex) => complex._id === savedComplex._id);

      if (!exists) {
        return [savedComplex, ...prev];
      }

      return prev.map((complex) =>
        complex._id === savedComplex._id
          ? { ...complex, ...savedComplex }
          : complex
      );
    });

    if (shouldClose) {
      setEditingComplex(null);
    } else {
      setEditingComplex(savedComplex);
    }
    invalidate(["complexes"]);
  };

  const handleCopyComplex = async (complex) => {
    await navigator.clipboard.writeText(JSON.stringify(complex, null, 2));
    setCopiedComplexId(complex._id);
    window.clearTimeout(copyFeedbackTimeoutRef.current);
    copyFeedbackTimeoutRef.current = window.setTimeout(() => setCopiedComplexId(null), 1200);
  };

  useEffect(() => () => window.clearTimeout(copyFeedbackTimeoutRef.current), []);

  const handleCloneComplex = (complex) => {
    openComplexForm("clone", createCloneComplexFormState(complex));
  };

  const handleRemoveComplex = async (complex) => {
    const title = complex.name || "Без названия";
    const confirmed = window.confirm(
      `Удалить документ "${title}"? Это действие нельзя отменить.`
    );

    if (!confirmed) {
      return;
    }

    await deleteAdminComplex(complex._id);
    notify(`ЖК «${title}» удалён`, "success");
    setSearchTotal((prev) => {
      const nextTotal = Math.max(0, prev - 1);
      const nextTotalPages = Math.max(1, Math.ceil(nextTotal / limit));

      if (page > nextTotalPages) {
        setPage(nextTotalPages);
      }

      return nextTotal;
    });
    invalidate(["complexes"]);
  };

  const openBulkModal = () => {
    setBulkSetValue("{\n  \"developer\": \"\"\n}");
    setBulkError("");
    setIsBulkOpen(true);
  };

  const handleBulkSubmit = async (event) => {
    event.preventDefault();
    setBulkError("");

    try {
      const set = bulkSetValue.trim() ? JSON.parse(bulkSetValue) : {};

      if (!isPlainObject(set)) {
        setBulkError("set должен быть JSON-объектом");
        return;
      }

      const unsafeFields = Object.keys(set).filter((field) => !SAFE_FILTERED_UPDATE_FIELDS.has(field));
      if (unsafeFields.length > 0) {
        setBulkError(`Update filtered разрешает только: ${Array.from(SAFE_FILTERED_UPDATE_FIELDS).join(", ")}. Недопустимо: ${unsafeFields.join(", ")}`);
        return;
      }



      const ids = filteredComplexes.map((complex) => complex._id).filter(Boolean);

      if (ids.length === 0) {
        setBulkError("Нет документов для обновления");
        return;
      }

      if (!window.confirm(`Обновить ${ids.length} документов?`)) {
        return;
      }

      setIsBulkSaving(true);
      await bulkPatchAdminComplexes({ ids, set });
      invalidate(["complexes"]);
      notify(`Обновлено ЖК: ${ids.length}`, "success");
    } catch (bulkUpdateError) {
      const message = bulkUpdateError instanceof Error
          ? bulkUpdateError.message
          : "Не удалось выполнить bulk update";
      setBulkError(message); notify(message, "error");
    } finally {
      setIsBulkSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(searchTotal / limit));
  const hasActiveFieldSearch = getActiveFieldFilters(fieldFilters).length > 0;
  const resetFieldSearch = () => {
    setFieldFilters([createFieldFilter()]);
    setPage(1);
  };
  const updateFieldFilter = (id, patch) => {
    setFieldFilters((current) => current.map((item) => (
      item.id === id ? { ...item, ...patch } : item
    )));
    setPage(1);
  };
  const addFieldFilter = () => {
    setFieldFilters((current) => [...current, createFieldFilter()]);
    setPage(1);
  };
  const removeFieldFilter = (id) => {
    setFieldFilters((current) => current.length > 1
      ? current.filter((item) => item.id !== id)
      : [createFieldFilter()]);
    setPage(1);
  };

  return (
    <>
        <div className="complexes-section-top" style={styles.fixedTop}>
        <div className="complexes-search-panel" style={styles.card}>
          <div className="workspace-selection-search-header">
            <div className="workspace-selection-search-header__saved">
              <SavedViewsControls
                sectionId="complexes"
                currentView={currentSavedView}
                defaultView={DEFAULT_COMPLEXES_VIEW}
                onApply={applySavedView}
              />
            </div>
            {isWorkspaceMode && (
              <WorkspaceSelectionModeBar
                count={searchTotal}
                missingCount={Math.max(0, workspaceIds.length - workspaceFoundIds.length)}
                onExit={exitWorkspaceMode}
              />
            )}
          </div>
          <div className="complexes-search-toolbar">
            <div className="complexes-search-toolbar__primary-actions">
              <button
                type="button"
                className="complexes-add-action"
                onClick={() => openComplexForm("create", createEmptyComplexFormState())}
              >
                + Добавить ЖК
              </button>
              <button
                type="button"
                className="complexes-filtered-action"
                onClick={openBulkModal}
              >
                Обновить несколько ЖК
              </button>
            </div>
            <div className="complexes-search-toolbar__actions">
              <p className="complexes-search-toolbar__count">
                Найдено: {searchTotal}, показано: {filteredComplexes.length}
              </p>
              <div className="complexes-view-switch" aria-label="Режим отображения">
                <button
                  type="button"
                  className={`complexes-view-switch__button${viewMode === "table" ? " complexes-view-switch__button--active" : ""}`}
                  aria-pressed={viewMode === "table"}
                  onClick={() => setViewMode("table")}
                >
                  Таблица
                </button>
                <button
                  type="button"
                  className={`complexes-view-switch__button${viewMode === "json" ? " complexes-view-switch__button--active" : ""}`}
                  aria-pressed={viewMode === "json"}
                  onClick={() => setViewMode("json")}
                >
                  JSON
                </button>
              </div>
            </div>
          </div>

          <div className="complexes-quick-filters">
            <label className="complexes-search-field complexes-search-field--main">
              <span className="complexes-search-label">Основной поиск</span>
              <input
                className="admin-search-control"
                placeholder="Название, город, застройщик или метро..."
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setPage(1);
                }}
              />
            </label>

            <label className="complexes-search-field">
              <span className="complexes-search-label">Тип ЖК</span>
              <select
                className="admin-search-control"
                value={category}
                onChange={(event) => {
                  setCategory(event.target.value);
                  setPage(1);
                }}
              >
                <option value="">Все типы</option>
                <option value="residential">Жилой комплекс</option>
                <option value="commercial">Коммерция</option>
                <option value="vehicles">Авто</option>
                <option value="special">Другое</option>
              </select>
            </label>

            <label className="complexes-search-field">
              <span className="complexes-search-label">Статус</span>
              <select
                className="admin-search-control"
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value);
                  setPage(1);
                }}
              >
                <option value="">Любой статус</option>
                <option value="active">Активные</option>
                <option value="stopped">В стопе</option>
              </select>
            </label>
          </div>

          <div className="complexes-search-lower-row">
          <div className="complexes-field-search">
            <div className="complexes-field-search__toolbar">
              <button
                type="button"
                className="complexes-field-search__toggle"
                aria-expanded={isFieldSearchOpen}
                aria-controls="complexes-field-search-fields"
                onClick={() => setIsFieldSearchOpen((value) => !value)}
              >
                <span aria-hidden="true">{isFieldSearchOpen ? "▼" : "▶"}</span>
                <span>Поиск по полю</span>
                {hasActiveFieldSearch && (
                  <span className="complexes-field-search__active-badge">активен</span>
                )}
              </button>
              {hasActiveFieldSearch && (
                <button
                  type="button"
                  className="complexes-field-search__reset"
                  onClick={resetFieldSearch}
                >
                  Сбросить
                </button>
              )}
            </div>
            <div
              id="complexes-field-search-fields"
              className={`complexes-field-search__collapse${isFieldSearchOpen ? " complexes-field-search__collapse--open" : ""}`}
              aria-hidden={!isFieldSearchOpen}
              inert={isFieldSearchOpen ? undefined : ""}
            >
              <div className="complexes-field-search__collapse-inner">
                <div className="complexes-field-search__conditions">
                  {fieldFilters.map((condition, index) => {
                    const hasValue = !["exists", "empty"].includes(condition.operator);
                    return (
                      <div className="complexes-field-search__row" key={condition.id}>
                        <label className="complexes-search-field">
                          <span className="complexes-search-label">Поле</span>
                          <select
                            className="admin-search-control"
                            value={condition.field}
                            onChange={(event) => updateFieldFilter(condition.id, {
                              field: event.target.value,
                              ...(event.target.value === "status" && condition.operator === "contains"
                                ? { operator: "equals", value: "" }
                                : event.target.value === "currentClientId"
                                  ? { operator: "equals", value: "" }
                                  : {}),
                            })}
                          >
                            <option value="">Выберите поле</option>
                            {FIELD_SEARCH_FIELDS.map((field) => (
                              <option key={field} value={field}>{FIELD_SEARCH_LABELS[field]}</option>
                            ))}
                          </select>
                        </label>

                        <label className="complexes-search-field">
                          <span className="complexes-search-label">Условие</span>
                          <select
                            className="admin-search-control"
                            value={condition.operator}
                            onChange={(event) => updateFieldFilter(condition.id, {
                              operator: event.target.value,
                              ...(["exists", "empty"].includes(event.target.value) ? { value: "" } : {}),
                            })}
                          >
                            {FIELD_SEARCH_OPERATORS
                              .filter((operator) => condition.field === "currentClientId"
                                ? ["equals", "not equals"].includes(operator)
                                : condition.field !== "status" || operator !== "contains")
                              .map((operator) => (
                              <option key={operator} value={operator}>{FIELD_SEARCH_OPERATOR_LABELS[operator]}</option>
                              ))}
                          </select>
                        </label>

                        <label className="complexes-search-field complexes-search-field--value">
                          <span className="complexes-search-label">Значение</span>
                          {condition.field === "currentClientId" && hasValue ? (
                            <select
                              className="admin-search-control"
                              value={condition.value}
                              onChange={(event) => updateFieldFilter(condition.id, { value: event.target.value })}
                            >
                              <option value="">Выберите клиента</option>
                              {sipClientOptions.map((client) => (
                                <option key={client._id} value={client._id}>{client.name}</option>
                              ))}
                            </select>
                          ) : condition.field === "status" && hasValue ? (
                            <select
                              className="admin-search-control"
                              value={condition.value}
                              onChange={(event) => updateFieldFilter(condition.id, { value: event.target.value })}
                            >
                              <option value="">Выберите статус</option>
                              <option value="true">Активен</option>
                              <option value="false">В стопе</option>
                            </select>
                          ) : (
                            <input
                              className="admin-search-control"
                              disabled={!hasValue}
                              placeholder={hasValue ? "Введите значение" : "Значение не требуется"}
                              value={condition.value}
                              onChange={(event) => updateFieldFilter(condition.id, { value: event.target.value })}
                            />
                          )}
                        </label>

                        <ActionIconButton
                          className="complexes-field-search__row-action"
                          icon={index === 0 ? "plus" : "close"}
                          label={index === 0 ? "Добавить условие поиска" : "Удалить условие поиска"}
                          title={index === 0 ? "Добавить условие" : "Удалить условие"}
                          tone={index === 0 ? "default" : "danger"}
                          onClick={index === 0 ? addFieldFilter : () => removeFieldFilter(condition.id)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="complexes-pagination">
            <span className="complexes-pagination__status">
              Страница {page} из {totalPages}
            </span>
            <div className="complexes-pagination__controls">
              <button
                type="button"
                className="complexes-pagination__button"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                Назад
              </button>

              <button
                type="button"
                className="complexes-pagination__button"
                disabled={page >= totalPages || isLoading}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              >
                Вперед
              </button>
              <label className="complexes-page-size">
                <span>На странице</span>
                <select
                  className="admin-search-control complexes-page-size__control"
                  value={limit}
                  onChange={(event) => {
                    setLimit(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                </select>
              </label>
            </div>
          </div>
          </div>

          </div>
        </div>

        <div className="complexes-documents-card" style={styles.documentsCard}>
          <div style={styles.documentsHeader}>
            <h2 style={styles.documentsTitle}>Документы</h2>
            <span style={styles.muted}>{filteredComplexes.length} / {searchTotal}</span>
          </div>

          <TemporarySelectionBar
            selectedItems={temporarySelection.selectedItems}
            onClear={temporarySelection.clearSelection}
            additionalActions={
              <BulkComplexTableEditorAction
                items={temporarySelection.selectedItems}
                onApplied={handleBulkComplexApplied}
                sourceSection="complexes"
                buttonStyle={styles.viewButton}
              />
            }
          />

          <div style={styles.documentsScroll}>
          {isLoading ? (
            <div style={styles.state}>Загрузка базы ЖК...</div>
          ) : error ? (
            <div style={styles.state}>{error}</div>
          ) : filteredComplexes.length === 0 ? (
            <div style={styles.state}>Ничего не найдено</div>
          ) : viewMode === "table" ? (
            <div className="complexes-table-wrap" style={styles.tableWrap}>
              <table className="complexes-table" style={styles.table}>
                <colgroup>
                  {COMPLEX_TABLE_COLUMN_WIDTHS.map((width, index) => (
                    <col key={index} style={{ width: `${width}%` }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, ...styles.nameCell }}>
                      <div className="complexes-table__name-with-selection">
                        <TemporarySelectionCheckbox
                          checked={temporarySelection.allVisibleSelected}
                          indeterminate={temporarySelection.partiallyVisibleSelected}
                          onChange={temporarySelection.toggleVisible}
                          label="Выбрать все видимые ЖК"
                        />
                        <span>Название</span>
                      </div>
                    </th>
                    <th style={{ ...styles.th, ...styles.cityCell }}>Город</th>
                    <th style={{ ...styles.th, ...styles.typeCell }}>Тип перевода</th>
                    <th style={{ ...styles.th, ...styles.transferCell }}>Номер перевода</th>
                    <th style={{ ...styles.th, ...styles.statusCell }}>Статус</th>
                    <th style={{ ...styles.th, ...styles.sipCell }}>Текущий клиент</th>
                    <th style={{ ...styles.th, ...styles.developerCell }}>Застройщик</th>
                    <th style={{ ...styles.th, ...styles.actionsCell }}>Действия</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredComplexes.map((complex, index) => {
                    const isActive = normalizeStatus(complex.status);
                    const typeText = htmlToPlainText(complex.type);
                    const rowBackground =
                      index % 2 === 0 ? "var(--card-bg)" : "var(--card-bg-soft)";

                    return (
                      <tr
                        key={complex._id}
                        className="complexes-table__document-row"
                        style={{ background: rowBackground }}
                        onMouseEnter={(event) => {
                          event.currentTarget.style.background = "var(--accent-soft)";
                        }}
                        onMouseLeave={(event) => {
                          event.currentTarget.style.background = rowBackground;
                        }}
                      >
                        <td className="complexes-table__cell complexes-table__cell--name" style={{ ...styles.td, ...styles.nameCell }}>
                          <div className="complexes-table__name-with-selection">
                            <TemporarySelectionCheckbox
                              checked={temporarySelection.isTemporarilySelected(visibleSelectionItems[index])}
                              onChange={() => temporarySelection.toggleOne(visibleSelectionItems[index])}
                              label={`Выбрать ЖК ${complex.name || "Без названия"}`}
                            />
                            <div className="admin-complex-name" style={styles.name} title={complex.name || "Без названия"} tabIndex={0}>
                              {complex.name || "Без названия"}
                            </div>
                          </div>
                        </td>

                        <td className="complexes-table__cell" style={{ ...styles.td, ...styles.cityCell }}>
                          <div className="complexes-table__clamped complexes-table__clamped--city" title={getCityName(complex.city, cityNameByKey)} tabIndex={0}>
                            {getCityName(complex.city, cityNameByKey)}
                          </div>
                        </td>

                        <td style={{ ...styles.td, ...styles.typeCell }}>
                          {typeText ? (
                            <div
                              className="complexes-table__type-text admin-cell-ellipsis"
                              style={styles.typeText}
                              title={typeText}
                              tabIndex={0}
                            >
                              {typeText}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>

                        <td className="complexes-table__cell complexes-table__cell--center" style={{ ...styles.td, ...styles.transferCell }}>
                          <div className="complexes-table__clamped complexes-table__clamped--short" title={String(complex.transfer_number || "—")} tabIndex={0}>
                            {complex.transfer_number || "—"}
                          </div>
                        </td>

                        <td className="complexes-table__cell complexes-table__cell--center" style={{ ...styles.td, ...styles.statusCell }}>
                          <AdminStatusBadge active={isActive} entity="complex" />
                        </td>

                        <td className="complexes-table__cell" style={{ ...styles.td, ...styles.sipCell }}>
                          <div className="complexes-table__clamped" title={getCurrentSipClientLabel(complex)} tabIndex={0}>
                            {getCurrentSipClientLabel(complex)}
                          </div>
                        </td>

                        <td className="complexes-table__cell" style={{ ...styles.td, ...styles.developerCell }}>
                          <div className="complexes-table__clamped complexes-table__clamped--developer" title={complex.developer || "—"} tabIndex={0}>
                            {complex.developer || "—"}
                          </div>
                        </td>

                        <td className="complexes-table__cell complexes-table__cell--actions" style={{ ...styles.td, ...styles.actionsCell, ...styles.actionsTd }}>
                          <div className="admin-row-actions">
                            <WorkspaceSelectionToggle
                              item={createComplexSelectionItem(complex, getCityName(complex.city, cityNameByKey))}
                            />
                            <ActionIconButton
                              icon="edit"
                              label="Редактировать ЖК"
                              tone="primary"
                              onClick={() => openComplexForm("edit", complex)}
                            />

                            <ActionIconButton
                              icon={copiedComplexId === complex._id ? "check" : "copy"}
                              label="Копировать JSON"
                              onClick={() => handleCopyComplex(complex)}
                            />

                            <ActionIconButton
                              icon="clone"
                              label="Клонировать ЖК"
                              onClick={() => handleCloneComplex(complex)}
                            />

                            <ActionIconButton
                              icon="trash"
                              label="Удалить ЖК"
                              tone="danger"
                              onClick={() => handleRemoveComplex(complex)}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={styles.jsonList}>
              {filteredComplexes.map((complex) => (
                <div key={complex._id} style={styles.jsonCard}>
                  <div style={styles.jsonHeader}>
                    <div>
                      <div style={styles.name}>
                        {complex.name || "Без названия"}
                      </div>
                    </div>

                    <div className="admin-row-actions">
                      <WorkspaceSelectionToggle
                        item={createComplexSelectionItem(complex, getCityName(complex.city, cityNameByKey))}
                      />
                      <ActionIconButton
                        icon="edit"
                        label="Редактировать ЖК"
                        tone="primary"
                        onClick={() => openComplexForm("edit", complex)}
                      />

                      <ActionIconButton
                        icon={copiedComplexId === complex._id ? "check" : "copy"}
                        label="Копировать JSON"
                        onClick={() => handleCopyComplex(complex)}
                      />

                      <ActionIconButton
                        icon="clone"
                        label="Клонировать ЖК"
                        onClick={() => handleCloneComplex(complex)}
                      />

                      <ActionIconButton
                        icon="trash"
                        label="Удалить ЖК"
                        tone="danger"
                        onClick={() => handleRemoveComplex(complex)}
                      />
                    </div>
                  </div>

                  <pre style={styles.pre}>{JSON.stringify(complex, null, 2)}</pre>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isBulkOpen ? (
        <div style={styles.modalOverlay}>
          <form
            style={styles.bulkModal}
            onSubmit={handleBulkSubmit}
          >
            <div style={styles.jsonHeader}>
              <div>
                <h2 style={styles.title}>Обновить несколько ЖК</h2>
                <p style={styles.muted}>
                  Документов: {filteredComplexes.length}
                </p>
              </div>
            </div>

            <p style={{ ...styles.placeholderText, color: "var(--danger)" }}>
              Legacy-инструмент применяет одно значение ко всем отфильтрованным ЖК. Разрешены только name, city, category, developer, location и presetBase. Для разных значений используйте табличный редактор.
            </p>

            {bulkError ? (
              <p style={styles.filterError}>{bulkError}</p>
            ) : null}

            <p style={styles.fieldSearchTitle}>set</p>
            <textarea
              disabled={isBulkSaving}
              spellCheck="false"
              style={styles.bulkTextarea}
              value={bulkSetValue}
              onChange={(event) => setBulkSetValue(event.target.value)}
            />


            <div style={{ ...styles.actionsRow, justifyContent: "flex-end", marginTop: "12px" }}>
              <button
                disabled={isBulkSaving}
                type="button"
                style={styles.viewButton}
                onClick={() => setIsBulkOpen(false)}
              >
                Отмена
              </button>

              <button
                disabled={isBulkSaving}
                type="submit"
                style={styles.button}
              >
                {isBulkSaving ? "Обновляется..." : "Сохранить изменение"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <EditComplexModal
        key={formSessionId}
        complex={editingComplex}
        mode={formMode}
        focusEmbeddedSipClientId={editingComplex?._focusEmbeddedSipClientId}
        isOpen={Boolean(editingComplex)}
        onClose={() => setEditingComplex(null)}
        onSaved={handleSaved}
      />
    </>
  );
}
